import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Save, Bot, AlertTriangle, ScrollText } from "lucide-react";

const LEVEL_COLORS = { green: "#4ADE80", yellow: "#FACC15", red: "#F87171" };
const ALERT_COLORS = { red: "#F87171", yellow: "#FACC15", info: "#38BDF8" };

export default function TreasuryController() {
  const [snap, setSnap] = useState(null);
  const [reserve, setReserve] = useState("");
  const [q, setQ] = useState("");
  const [chat, setChat] = useState([]);
  const [busy, setBusy] = useState(null);
  const [auditLog, setAuditLog] = useState(null);

  const load = () => {
    api.get("/admin/treasury").then(({ data }) => { setSnap(data); setReserve(String(data.min_reserve_pct)); }).catch(() => {});
  };
  useEffect(load, []);

  const saveReserve = async () => {
    setBusy("reserve");
    try {
      await api.put("/admin/treasury/settings", { min_reserve_pct: Number(reserve) || 0 });
      toast.success(`Minimum reserve target set to ${reserve}%`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const ask = async () => {
    const question = q.trim();
    if (!question) return;
    setBusy("ask");
    setChat((c) => [...c, { role: "you", text: question }]);
    setQ("");
    try {
      const { data } = await api.post("/admin/treasury/ask", { question });
      setChat((c) => [...c, { role: "ai", text: data.answer }]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const toggleAudit = () => {
    if (auditLog) { setAuditLog(null); return; }
    api.get("/admin/treasury/audit").then(({ data }) => setAuditLog(data)).catch(() => {});
  };

  if (!snap) return null;
  const col = LEVEL_COLORS[snap.health_level];

  return (
    <div className="space-y-5" data-testid="treasury-controller">
      <div className="flex flex-wrap items-stretch gap-3">
        <div className="border p-4 flex-1 min-w-[220px]" style={{ borderColor: `${col}66`, background: `${col}0d` }} data-testid="treasury-health">
          <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Ecosystem Health</div>
          <div className="flex items-end gap-3">
            <span className="font-display font-black text-4xl" style={{ color: col }} data-testid="treasury-health-score">{snap.health_score}</span>
            <span className="text-[10px] font-black uppercase tracking-widest mb-1.5 px-2 py-0.5 border"
              style={{ color: col, borderColor: `${col}88` }} data-testid="treasury-health-level">{snap.health_level}</span>
          </div>
          <div className="text-[10px] text-zinc-400 mt-2">Coverage ratio <span className="font-mono font-bold" style={{ color: col }}>{snap.coverage_ratio}</span> · reserve {snap.reserve_pct}%</div>
        </div>
        {[["Available Cash", `$${snap.available_cash.toFixed(2)}`, "#4ADE80"],
          ["Obligations", `$${snap.obligations.toFixed(2)}`, "#FACC15"],
          ["Coin Liability", `${snap.coins_outstanding.toFixed(0)} BLC ≈ $${snap.coin_liability_usd.toFixed(2)}`, "#38BDF8"],
          ["Pending Settlements", `$${snap.pending_settlements.toFixed(2)} (${snap.pending_redemptions})`, "#F87171"]].map(([l, v, c]) => (
          <div key={l} className="bg-pb-elevated border border-pb-border p-4 flex-1 min-w-[160px]">
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</div>
            <div className="font-display font-black text-lg" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Revenue Today", `$${snap.revenue_today.toFixed(2)}`], ["Revenue This Month", `$${snap.revenue_month.toFixed(2)}`],
          ["Purse Paid Out", `$${(snap.purse_paid_out || 0).toFixed(2)}`],
          ["Active Merchants", `${snap.active_merchants}${snap.pending_merchants ? ` (+${snap.pending_merchants} pending)` : ""}`],
          ["Tx Volume (30d)", snap.tx_volume_30d]].map(([l, v]) => (
          <div key={l} className="bg-pb-elevated/50 border border-pb-border p-3">
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</div>
            <div className="font-mono font-bold text-sm">{v}</div>
          </div>
        ))}
      </div>

      <div className="border border-pb-border bg-pb-elevated/40 p-4" data-testid="treasury-recommendation">
        <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">AI Recommendation · Payout Policy</div>
        <p className="text-sm text-zinc-200">{snap.recommendation}</p>
        <p className="text-[11px] text-zinc-500 mt-1.5">{snap.payout_policy}</p>
      </div>

      {snap.alerts.length > 0 && (
        <div data-testid="treasury-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-black">Alert Center ({snap.alerts.length})</span>
          </div>
          <div className="space-y-1.5">
            {snap.alerts.map((a, i) => (
              <div key={i} className="flex items-start gap-2 border px-3 py-2 text-xs"
                style={{ borderColor: `${ALERT_COLORS[a.level]}55`, background: `${ALERT_COLORS[a.level]}0d` }}>
                <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: ALERT_COLORS[a.level] }} />
                <span className="text-zinc-300">{a.msg}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Minimum reserve target (%)</label>
          <input data-testid="treasury-reserve-input" type="number" min={0} max={90} step={1} value={reserve}
            onChange={(e) => setReserve(e.target.value)}
            className="w-24 bg-pb-elevated border border-pb-border px-3 py-2.5 text-white font-mono text-sm focus:outline-none focus:border-pb-red" />
        </div>
        <button data-testid="treasury-reserve-save" onClick={saveReserve} disabled={!!busy}
          className="flex items-center gap-1.5 bg-pb-elevated border border-pb-border font-black uppercase tracking-widest px-4 py-3 text-[10px] hover:border-pb-red hover:text-pb-red transition-colors disabled:opacity-50">
          {busy === "reserve" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
        <button data-testid="treasury-audit-toggle" onClick={toggleAudit}
          className="flex items-center gap-1.5 bg-pb-elevated border border-pb-border font-black uppercase tracking-widest px-4 py-3 text-[10px] hover:border-zinc-400 transition-colors">
          <ScrollText className="w-3.5 h-3.5" /> Audit Log
        </button>
      </div>

      {auditLog && (
        <div className="divide-y divide-pb-border/40 border border-pb-border max-h-52 overflow-y-auto" data-testid="treasury-audit-log">
          {auditLog.length === 0 && <div className="p-3 text-xs text-zinc-600">No admin actions recorded yet.</div>}
          {auditLog.map((a) => (
            <div key={a.id} className="px-3 py-2 text-xs flex flex-wrap justify-between gap-2">
              <span><span className="font-bold text-zinc-300">{a.action}</span> <span className="text-zinc-500">— {a.detail}{a.reason ? ` (${a.reason})` : ""}</span></span>
              <span className="text-zinc-600">{a.admin_name} · {new Date(a.at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      <div className="border border-pb-cyan/30 bg-pb-cyan/5 p-4" data-testid="treasury-ai-center">
        <div className="flex items-center gap-2 mb-3">
          <Bot className="w-4 h-4 text-pb-cyan" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 font-black">AI Command Center</span>
        </div>
        {chat.length > 0 && (
          <div className="space-y-2 mb-3 max-h-64 overflow-y-auto" data-testid="treasury-ai-chat">
            {chat.map((m, i) => (
              <div key={i} className={`text-xs px-3 py-2 whitespace-pre-line ${m.role === "you" ? "bg-pb-elevated text-zinc-300 border border-pb-border" : "bg-pb-cyan/10 border border-pb-cyan/30 text-zinc-200"}`}>
                <span className="text-[8px] font-black uppercase tracking-widest block mb-0.5" style={{ color: m.role === "you" ? "#A1A1AA" : "#22D3EE" }}>{m.role === "you" ? "You" : "Treasury AI"}</span>
                {m.text}
              </div>
            ))}
            {busy === "ask" && <div className="text-xs text-pb-cyan animate-pulse px-3">Treasury AI is analyzing…</div>}
          </div>
        )}
        <div className="flex gap-2">
          <input data-testid="treasury-ai-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder='Ask: "How is ecosystem health today?" · "Can we safely increase payouts?"'
            className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-cyan" />
          <button data-testid="treasury-ai-send" onClick={ask} disabled={busy === "ask" || !q.trim()}
            className="flex items-center gap-1.5 bg-pb-cyan text-black font-black uppercase tracking-widest px-4 py-2.5 text-[10px] hover:bg-[#BEF264] transition-colors disabled:opacity-40">
            {busy === "ask" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Ask
          </button>
        </div>
      </div>
    </div>
  );
}
